import Head from 'next/head';
import { usePathname } from 'next/navigation';
import styles from '../styles/Layout.module.css';
import { FAQ, MetaData, PageProps } from '../types';
import { renderFAQSchema, renderSchema } from '../utils/schema';
import Footer from './Footer';
import Header from './Header';

const metaDefaults: MetaData = {
  title: '',
  description: '',
  image: '/ogimage.png',
};

interface LayoutProps extends PageProps {
  h1: string;
  meta?: MetaData;
  faqs?: FAQ[];
  // Describes the page for search engines, e.g. a keyboard as a web app
  schemaType?: 'WebApplication' | 'WebSite';
  // Keep the page out of search results
  noindex?: boolean;
  // Just the page's content, without the header, menu and footer, for
  // keyboards embedded in other sites
  bare?: boolean;
  // Shows the affiliate disclosure in the footer
  affiliateLinks?: boolean;
  // For search engines: the keyboard's language (BCP 47, e.g. ru) and when
  // it last changed (YYYY-MM-DD)
  inLanguage?: string;
  dateModified?: string;
  children?: React.ReactNode;
}

const SITE_NAME = 'Language Keyboard';

// Who runs the site, for search engines
const publisher = (baseURL: string) => ({ '@type': 'Person', name: 'Will', url: `${baseURL}/about` });

const Layout = (props: LayoutProps) => {
  const {
    globalContext,
    h1,
    meta,
    faqs,
    schemaType,
    noindex,
    bare,
    affiliateLinks,
    inLanguage,
    dateModified,
    children,
  } = props;
  const { baseURL, menu, keyboardGroups } = globalContext;
  const canonicalUrl = baseURL + usePathname();
  const description = meta?.description || metaDefaults.description;
  const image = baseURL + (meta?.image || metaDefaults.image);
  const title = meta?.title || h1 || metaDefaults.title;
  // No height=device-height: iPhones can then size the page wrongly and leave
  // its top under the status bar after scrolling
  const viewport = 'width=device-width, initial-scale=1.0, user-scalable=yes';
  return (
    <>
      <Head>
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.webmanifest" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <meta name="apple-mobile-web-app-title" content="Keyboards" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link rel="preconnect" href="https://pagead2.googlesyndication.com" />
        <title>{title}</title>
        <meta name="viewport" content={viewport} />
        <meta name="description" content={description} />
        {noindex && <meta name="robots" content="noindex" />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta name="theme-color" content="#074038" media="(prefers-color-scheme: dark)" />
        <meta name="theme-color" content="#0c6457" media="(prefers-color-scheme: light)" />
        {schemaType === 'WebApplication' &&
          renderSchema({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: h1,
            url: canonicalUrl,
            description,
            applicationCategory: 'UtilitiesApplication',
            operatingSystem: 'Any',
            browserRequirements: 'Requires JavaScript',
            isAccessibleForFree: true,
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            ...(inLanguage ? { inLanguage } : {}),
            ...(dateModified ? { dateModified } : {}),
            publisher: publisher(baseURL),
          })}
        {schemaType === 'WebApplication' &&
          renderSchema({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${baseURL}/` },
              { '@type': 'ListItem', position: 2, name: h1, item: canonicalUrl },
            ],
          })}
        {schemaType === 'WebSite' &&
          renderSchema({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_NAME,
            url: canonicalUrl,
            description,
            inLanguage: 'en',
            publisher: publisher(baseURL),
          })}
        {renderFAQSchema(faqs)}
      </Head>
      {!bare && (
        // For keyboard users: the first Tab offers to jump past the header
        <a href="#main" className={styles.skipLink}>
          Skip to content
        </a>
      )}
      {!bare && <Header h1={h1} menu={menu} keyboardGroups={keyboardGroups} />}
      {/* tabIndex lets the skip link move the focus here, not just scroll */}
      <main id="main" tabIndex={-1} className={bare ? styles.bare : styles.main}>
        {children}
      </main>
      {!bare && <Footer affiliateLinks={affiliateLinks} />}
    </>
  );
};

export default Layout;
